<template>
  <SettingsPagePanel>
    <PlannerEditorHeader
      :planner="planner"
      :isEditing="isEditing"
      :isButtonDisabled="isButtonDisabled"
      @buttonClick="handleButtonClick"
      @cancelButtonClick="handleCancenlButtonClick"
    />

    <SettingsTable :isEditing="isEditing">
      <SettingsTableRow name="登録申請されたメールアドレス">
        <p :class="$style.text">{{ planner.email }}</p>
      </SettingsTableRow>

      <SettingsTableRow name="プロフィール画像">
        <PlannerEditorTableAvatarInput
          :modelValue="avatarImage"
          :preview="!isEditing"
          :isChanged="isAvatarChanged"
          :disabled="isFetchingAvatarImage"
          @update:modelValue="uploadPlannerAvatarImage"
          @revert="form.avatarImage = planner.avatarImage"
        />
        <template #rowAfter>
          <div :class="[$style.row__info, $style.info]" v-if="isEditing">
            <IconInfo :class="$style.info__icon" />
            <span :class="$style.info__text">
              .jpg / .png / .gif をご利用いただけます。
            </span>
          </div>
        </template>
      </SettingsTableRow>

      <SettingsTableRow name="カバー画像">
        <PlannerEditorTableCoverInput
          :coverImage="form.coverImage"
          :preview="!isEditing"
          @input="handleCoverInput"
        />
        <template #rowAfter>
          <div :class="[$style.row__info, $style.info]" v-if="isEditing">
            <IconInfo :class="$style.info__icon" />
            <span :class="$style.info__text">
              .jpg / .png / .gif をご利用いただけます。
            </span>
          </div>
        </template>
      </SettingsTableRow>

      <SettingsTableRow name="性別">
        <PlannerEditorTableSexInput v-model="form.sex" :isEditing="isEditing" />
      </SettingsTableRow>

      <SettingsTableRow name="お子様">
        <template #poptip>
          <HelpPoptip :class="$style.title__poptip">
            <p :class="[$style.tips, $style['tips--onlineMatching']]">
              ご入力いただくと、属性が近いユーザーの興味を引きやすくなります。
            </p>
          </HelpPoptip>
        </template>
        <PlannerEditorTableChildrenInput
          v-model="form.hasChild"
          :isEditing="isEditing"
        />
        <PlannerEditorTableChildrenDetailInput
          v-model="form.children"
          :isEditing="isEditing"
          :hasChild="form.hasChild"
          :handleInvalidKeys="handleInvalidKeys"
        />
      </SettingsTableRow>

      <SettingsTableRow name="お住まいの地域">
        <p :class="$style.text">{{ addressString }}</p>
        <template #poptip>
          <HelpPoptip :class="$style.title__poptip">
            <p :class="[$style.tips, $style['tips--onlineMatching']]">
              ご入力いただくと、属性が近いユーザーの興味を引きやすくなります。
            </p>
          </HelpPoptip>
        </template>
        <template #editor>
          <span>〒 </span>
          <DolphinTextInput type="text" v-model="postalCodeRef" />
        </template>
        <template #rowAfter>
          <div v-if="isEditing">
            <p :class="[$style.row__info, $style['explanation']]">
              ※ハイフン無しで郵便番号を入力してください。<br />
              ※郵便番号および住所の（）の部分は、ユーザーに公開されません。
            </p>
            <p :class="[$style.row__info, $style.address]">
              {{ addressPreviewString }}
            </p>
          </div>
        </template>
      </SettingsTableRow>

      <SettingsTableRow name="生年月日">
        <p :class="$style.text">{{ birthdayString }}</p>
        <template #editor>
          <DolphinTextInput type="date" v-model="form.birthday" />
        </template>
      </SettingsTableRow>

      <SettingsTableRow name="所属企業">
        <p :class="$style.text">{{ corporation.name }}</p>
      </SettingsTableRow>

      <SettingsTableRow name="所属支社名">
        <p :class="$style.text">{{ branchNameString }}</p>
      </SettingsTableRow>

      <SettingsTableRow name="対応可能地域">
        <PlannerEditorPrefecturesInput
          v-model="form.availablePrefectureCodes"
          :preview="!isEditing"
        />
      </SettingsTableRow>

      <SettingsTableRow name="対応可能時間">
        <p :class="$style.text">{{ form.availableTimeText }}</p>
        <template #editor>
          <DolphinTextarea
            placeholder="例）平日 10:00〜19:00 / 土日祝日 9:00〜17:00"
            :rows="8"
            v-model="form.availableTimeText"
          />
        </template>
      </SettingsTableRow>

      <SettingsTableRow name="得意分野">
        <PlannerEditorSpecialtiesInput
          v-model="form.specialties"
          :options="selectableSpecialties"
          :preview="!isEditing"
        >
          <p :class="[$style.value, $style['value--empty']]">
            入力されていません
          </p>
        </PlannerEditorSpecialtiesInput>
      </SettingsTableRow>

      <SettingsTableRow name="自己紹介">
        <p :class="$style.selfIntroductionText">
          {{ form.selfIntroductionText }}
        </p>
        <template #editor>
          <CountingTextarea
            placeholder="例）長年、金融機関でファイナンシャルプランナーとして個人のお客様の相談業務に携わってきました。現在は独立系FP会社にて、個人のコンサルティング業務やマネーセミナーの講師活動をしております。(NISAやDCの活用など)"
            :rows="8"
            v-model="form.selfIntroductionText"
          />
        </template>
        <template #rowAfter>
          <p
            :class="[$style.row__info, $style['explanation']]"
            v-if="isEditing"
          >
            ※マッチング対象としてご希望するユーザー像がある場合には、プロフィールにわかりやすく明記をお願いします。例えば
            <span :class="$style['explanation__keyword']">
              「金融資産2000万円以上の方のみ」「女性のみ」
            </span>
            など。ご不明点があれば運営までお問合わせください。
          </p>
        </template>
      </SettingsTableRow>

      <!-- LINE 連携プランナーはオカネコ詳細画面を使わないので、QR コードを非表示にする -->
      <SettingsTableRow
        name="QRコード"
        v-if="!viewer.hasLineActivationProvider"
      >
        <PlannerQr :class="$style.qr" :plannerId="planner.id" />
      </SettingsTableRow>
    </SettingsTable>
  </SettingsPagePanel>
</template>

<script lang="ts">
import PlannerQr from '@/components/molecules/PlannerQr.vue'
import type { Planner, Corporation, Specialty, Sex, Branch } from '@/types/app'
import * as Types from '@/types/schema.generated'
import type { PlannerEditorTableViewerFragment } from './PlannerEditorTable.generated'
import { nanoid } from 'nanoid'
import { getAPIBaseURL } from '@/config'

export type FormValue = {
  planner: Planner
  coverImageFile: Maybe<File>
  childrenState: Types.ChildrenStateInput
  address: Types.InputMaybe<Types.AddressInput>
}
</script>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'

import IconInfo from '@/components/icons/IconInfo.vue'
import HelpPoptip from '@/components/molecules/HelpPoptip.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinTextarea from '@/components/atoms/DolphinTextarea.vue'
import CountingTextarea from '@/components/molecules/CountingTextarea.vue'

import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsTable from '@/pages/SettingsPage/SettingsTable/SettingsTable.vue'
import SettingsTableRow from '@/pages/SettingsPage/SettingsTable/SettingsTableRow.vue'

import PlannerEditorTableSexInput from './PlannerEditorTableSexInput.vue'
import PlannerEditorTableChildrenInput from './PlannerEditorTableChildrenInput.vue'
import PlannerEditorTableChildrenDetailInput from './PlannerEditorTableChildrenDetailInput.vue'
import PlannerEditorTableAvatarInput from './PlannerEditorTableAvatarInput.vue'
import PlannerEditorSpecialtiesInput from './PlannerEditorSpecialtiesInput.vue'
import PlannerEditorPrefecturesInput from './PlannerEditorPrefecturesInput.vue'
import PlannerEditorHeader from './PlannerEditorHeader.vue'
import PlannerEditorTableCoverInput from './PlannerEditorTableCoverInput.vue'

import { useIssuePlannerAvatarImageUploadUrlMutation } from './SettingsPagePlannerView.generated'
import axios from 'axios'

import type { FlexImage } from '@/libs/utilities/FlexImage'
import { generateFlexImage, isImageExtension } from '@/libs/utilities/FlexImage'
import { getFileExtension } from '@/libs/utilities/getFileExtension'
import { formatDateJa, formatKebabDate } from '@/libs/utilities/formatDate'
import type { Jisx0402Code } from '@/features/prefecture/jisx0402'

import { captureException as newrelicCaptureException } from '@/newrelic'
import { useAlert } from '@/libs/alertPortal'
import * as kenall from '@/hooks/useKenAll'

type Child = {
  gender: Types.Gender
  birthday: DateString
}

type FormChild = Child & {
  key: string
}

type Form = {
  avatarImage: Maybe<FlexImage>
  coverImage: Maybe<FlexImage>
  email: string
  sex: Sex
  hasChild: Maybe<Types.ChildrenStateAnswer>
  children: FormChild[]
  address: Maybe<Types.Address> | null
  birthday: string
  availableTimeText: string
  availablePrefectureCodes: Jisx0402Code[]
  specialties: readonly Specialty[]
  selfIntroductionText: string
}

const formalizePlanner = (
  planner: Planner,
  viewer: PlannerEditorTableViewerFragment
): Form => {
  return {
    ...planner,
    hasChild:
      viewer.profile.childrenState.answer ===
      Types.ChildrenStateAnswer.Unanswered
        ? undefined
        : viewer.profile.childrenState.answer,
    children: viewer.profile.childrenState.children.map(child => ({
      // __typename が Form には入らないようにする
      key: nanoid(),
      birthday: child.birthday,
      gender: child.gender,
    })),
    address: viewer.profile.address
      ? {
          city: viewer.profile.address.city,
          postalCode: viewer.profile.address.postalCode,
          prefecture: viewer.profile.address.prefecture,
          town: viewer.profile.address.town,
        }
      : null,
    birthday: formatKebabDate(planner.birthday),
    avatarImage: planner.avatarImage?.clone(),
    coverImage: planner.coverImage?.clone(),
    availablePrefectureCodes: [...planner.availablePrefectureCodes],
    specialties: [...planner.specialties],
  }
}

const props = withDefaults(
  defineProps<{
    planner: Planner
    viewer: PlannerEditorTableViewerFragment
    corporation: Corporation
    branches: Branch[]
    selectableSpecialties: ReadonlyArray<Specialty>
    isEditing?: boolean
    isButtonDisabled?: boolean
  }>(),
  {
    isEditing: false,
    isButtonDisabled: false,
  }
)

const emit = defineEmits<{
  (event: 'edit'): void
  (event: 'save', value: FormValue): void
  (event: 'cancel'): void
}>()

const isFetchingAvatarImage = ref(false)
const form = ref<Form>(formalizePlanner(props.planner, props.viewer))
const coverImageFile = ref<Maybe<File>>(undefined)

const resetForm = () => {
  coverImageFile.value = undefined
  form.value = formalizePlanner(props.planner, props.viewer)
}

const handleCoverInput = (file: File) => {
  coverImageFile.value = file
}

const isAvatarChanged = computed(() => {
  return avatarImage.value?.toString() !== props.planner.avatarImage?.toString()
})

const addressString = computed(() => {
  if (form.value.address == null) {
    return '未回答'
  }
  const address = form.value.address
  return `〒${address.postalCode} ${address.prefecture} ${address.city} ${address.town}`
})

const addressPreviewString = computed(() => {
  if (form.value.address == null) {
    return '未回答'
  }
  const address = form.value.address
  return `${address.prefecture} ${address.city} （${address.town}）`
})

const postalCodeRef = ref<string>(
  props.viewer.profile.address?.postalCode ?? ''
)

watch(
  postalCodeRef,
  async (postalCode: string) => {
    const normalizedPostalCode = postalCode
      .replace(/[-−]/g, '')
      .replace(/[０-９]+/g, s =>
        String.fromCharCode(...s.split('').map(c => c.charCodeAt(0) - 0xfee0))
      )

    if (normalizedPostalCode.length !== 7) {
      form.value.address = null
      return
    }

    try {
      const addressArray = await kenall.getAddress(normalizedPostalCode)
      // 住所が複数ある場合は、最初のものを選択する
      const address = addressArray.data[0] ?? null

      if (address === null) {
        return
      }

      form.value.address = {
        postalCode: address.postal_code,
        prefecture: address.prefecture,
        city: address.city,
        town: address.town,
      }
    } catch (e) {
      alert.error('住所の取得に失敗しました')
      newrelicCaptureException(e)
    }
  },
  { immediate: true }
)

const avatarImage = computed<Maybe<FlexImage>>(() => form.value.avatarImage)

const newFormValue = computed<FormValue>(() => {
  const birthday = new Date(form.value.birthday)
  return {
    planner: {
      ...props.planner,
      ...form.value,
      birthday,
    },
    coverImageFile: coverImageFile.value,
    childrenState: {
      answer: form.value.hasChild ?? Types.ChildrenStateAnswer.Unanswered,
      children: form.value.children.map(child => {
        // GraphQL スキーマの Child にないプロパティ： key を送るとエラーになるので key を取り除く
        const { key: _, ...rest } = child
        return rest
      }),
    },
    address: form.value.address ?? null,
  }
})

const alert = useAlert()

const imgixDomain = new URL(getAPIBaseURL('imgix')).host

const { mutate: issuePlannerAvatarImageUploadUrl } =
  useIssuePlannerAvatarImageUploadUrlMutation({})

const uploadPlannerAvatarImage = async (file: File | undefined) => {
  if (!file) {
    return
  }

  isFetchingAvatarImage.value = true

  const ext = getFileExtension(file)
  if (!isImageExtension(ext)) {
    alert.error('画像ファイルを選択してください')
    isFetchingAvatarImage.value = false
    return
  }

  const res = await issuePlannerAvatarImageUploadUrl({
    ext: ext,
  })

  const data = res?.data?.issuePlannerAvatarImageUploadUrl
  if (!data) {
    alert.error('画像のアップロードに失敗しました。')
    isFetchingAvatarImage.value = false
    return
  }

  await axios.put(data.uploadURL, file)

  form.value.avatarImage = generateFlexImage(imgixDomain, data.path)

  isFetchingAvatarImage.value = false
}

const birthdayString = computed(() =>
  formatDateJa(new Date(form.value.birthday))
)

const branchNameString = computed(() =>
  props.branches.map(({ name }) => name).join(', ')
)

const childrenBirthdayInvalidKeys = ref<string[]>([])

const handleInvalidKeys = (value: string[]) => {
  childrenBirthdayInvalidKeys.value = value
}

// NOTE: フォームのバリデーションが追加になったらここに追加する
const formValidationErrors = computed(() =>
  [
    {
      invalid: postalCodeRef.value.length !== 0 && form.value.address == null,
      errorText: 'お住まいの地域に入力された郵便番号は正しくありません',
    },
    {
      invalid: form.value.birthday === '',
      errorText: '生年月日を入力してください',
    },
    {
      invalid: isNaN(new Date(form.value.birthday).getTime()),
      errorText: '生年月日に入力された日付は正しくありません',
    },
    {
      invalid: childrenBirthdayInvalidKeys.value.length > 0,
      errorText: `${childrenBirthdayInvalidKeys.value
        .map(
          key =>
            `${
              form.value.children.findIndex(child => child.key === key) + 1
            }人目`
        )
        .join(', ')}のお子様の生年月日が正しくありません`,
    },
  ].filter(({ invalid }) => invalid)
)

const handleButtonClick = () => {
  if (!props.isEditing) {
    emit('edit')
    return
  }

  if (formValidationErrors.value.length > 0) {
    formValidationErrors.value.forEach(error => alert.error(error.errorText))
    return
  }

  emit('save', newFormValue.value)
}

const handleCancenlButtonClick = () => {
  emit('cancel')
}

watch(
  () => [props.planner, props.viewer] as const,
  value => {
    form.value = formalizePlanner(value[0], value[1])
  }
)

watch(
  () => props.isEditing,
  value => {
    if (!value) {
      resetForm()
    }
  }
)
</script>

<style lang="scss" module>
.row {
  &__info {
    margin-top: 16px;
  }
}

.title {
  &__poptip {
    display: inline-block;

    &:nth-child(n + 2) {
      margin-left: 8px;
    }
  }
}

.text {
  color: $color-text-and-object-high-emphasis;
  font-size: 16px;
  line-height: 28px;

  &--empty {
    color: $color-text-and-object-low-emphasis;
    font-size: 16px;
    line-height: 28px;
  }
}

.selfIntroductionText {
  font-size: 16px;
  white-space: pre-wrap;
}

.tips {
  overflow-wrap: break-word;

  &--onlineMatching {
    width: 200px;
  }

  &--matchingFilters {
    width: 200px;
  }
}

.info {
  display: flex;
  align-items: center;

  &__icon {
    color: $color-text-and-object-accent-primary;
    font-size: 14px;
  }

  &__text {
    color: $color-text-and-object-medium-emphasis;
    font-size: 12px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}

.explanation {
  align-items: center;
  color: $color-text-and-object-high-emphasis;
  font-size: 12px;

  &__keyword {
    font-weight: bold;
  }
}

.options {
  $gutter-horizontal: 40px;
  $gutter-vertical: 24px;

  display: flex;
  flex-wrap: wrap;
  margin-top: -$gutter-vertical;
  margin-left: -$gutter-horizontal;

  &__item {
    margin-top: $gutter-vertical;
    margin-left: $gutter-horizontal;
  }
}

.option {
  display: inline-flex;
  align-items: center;

  &__text {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}

.address {
  font-size: 16px;
}
</style>
